import { api } from './api.js';

/**
 * Download an asset (or miniature) file directly. Asks the API for a short-lived
 * download URL — a presigned S3 URL in prod, a local /cdn URL in lcl, both
 * carrying Content-Disposition: attachment — then navigates to it so the
 * browser saves the file. This avoids fetching the CDN URL directly, which
 * fails in prod because CloudFront caches a no-CORS copy of the image.
 *
 * Fails loudly: `api.get` throws on a non-OK response.
 * @param {string} assetId the asset/miniature id
 * @param {{ source?: string, version?: number, kind?: 'input' | 'output', role?: string }} [opts]
 *   selects the version (source and version; omit for the current one) and its
 *   file (kind and role; omit for the file the version shows)
 * @returns {Promise<void>}
 */
export async function downloadAssetFile(assetId, { source, version, kind, role } = {}) {
    const params = new URLSearchParams();
    if (source != null) params.set('source', source);
    if (version != null) params.set('version', String(version));
    if (kind != null) params.set('kind', kind);
    if (role != null) params.set('role', role);
    const query = params.toString();

    const { url } = await api.get(`/api/assets/${assetId}/download${query ? `?${query}` : ''}`);
    navigateTo(url);
}

/**
 * Download one of a catalog asset's d3 images the same way as
 * downloadAssetFile.
 *
 * Fails loudly: `api.get` throws on a non-OK response.
 * @param {string} assetId the catalog asset's id
 * @param {number} [version] the image version; omit for the current image
 * @returns {Promise<void>}
 */
export async function downloadCatalogFile(assetId, version) {
    const query = version != null ? `?version=${version}` : '';
    const { url } = await api.get(`/api/catalog/${assetId}/download${query}`);
    navigateTo(url);
}

/** @param {string} url */
function navigateTo(url) {
    const a = document.createElement('a');
    a.href = url;
    document.body.appendChild(a);
    a.click();
    a.remove();
}
